/* eslint-disable no-unused-vars */
import React from "react";
import { graphql, navigate } from "gatsby";
import Layout from "../components/layout";
import { Section, Sections, IPage } from "../interfaces";
import SEO from "../components/seo";
import SectionBroker from "../components/brokers/section-broker";
import { getSiteClassName } from "../utils";
import { Helmet } from "react-helmet";
import { login, isAuthenticated, isAuthorized, getProfile, authEnabled } from "../utils/auth";

export default class DefaultPage extends React.Component {
  trimmedPageName: string;
  siteClassName: string;
  protectedPath: boolean;

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    if (authEnabled() && (this.props["path"] || "").match(/^\/secure/)) {
      console.warn("Page is secure");
      if (!isAuthenticated()) {
        console.warn("User not authenticated.");
        login();
        return <p>Redirecting to login...</p>;
      } else if (!isAuthorized()) {
        console.warn("User not authorized to access this resource.");
        navigate("/");
        return <p>Access denied...</p>;
      }
    }
  }

  preventPageSEOIndexing() {
    const { search_engine_visibility } = this.props.data.cms.page;
    if (search_engine_visibility === "public" || search_engine_visibility == null) return null;
    return (
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
    );
  }

  /**
   * Group like siblings in a multidimensional array.
   *
   * This function takes an array of objects and groups adjacent objects with the
   * same '__typename' property into sub-arrays. Objects with unique 'component'
   * values in their immediate vicinity are left as individual elements.
   * The order of elements in the original array is preserved in the output.
   *
   * @param {Object[]} arr - The array of objects to be grouped. Each object should have at least a 'component' property.
   * @returns {Array} A multidimensional array where adjacent objects with the same 'component' are grouped together.
   *
   * @example
   * // returns [{'id': 1, 'component': 'hero'}, [{'id': 2, 'component': 'icon-row'}, {'id': 3, 'component': 'icon-row'}], {'id': 4, 'component': 'hero'}]
   * groupSectionsByComponentType([
   *   { id: 1, __typename: "hero" },
   *   { id: 2, __typename: "icon-row" },
   *   { id: 3, __typename: "icon-row" },
   *   { id: 4, __typename: "hero" },
   * ]);
   */
  groupSectionsByComponentType = (arr: any = []) => {
    if (!arr || arr.length === 0) return [];

    const result: any[] = [];
    let currentGroup = [arr[0]];

    for (let i = 1; i < arr.length; i++) {
      if (arr[i].__typename === arr[i - 1].__typename) {
        currentGroup.push(arr[i]);
      } else {
        if (currentGroup.length > 1) {
          result.push(currentGroup);
        } else {
          result.push(...currentGroup);
        }
        currentGroup = [arr[i]];
      }
    }

    if (currentGroup.length > 1) {
      result.push(currentGroup);
    } else {
      result.push(...currentGroup);
    }

    return result;
  };

  render() {
    const {
      attributes: { site, seo_description, seo_title, header, footer, id, sections, category }
    }: any = this.props.data.cms.page.data;

    const sectionGroups = this.groupSectionsByComponentType(sections);
    this.siteClassName = getSiteClassName(site);
    const user = getProfile();
    return (
      <Layout header={header} footer={footer} site={site} user={user}>
        <SEO
          site={site}
          description={seo_description}
          lang={process.env.GATSBY_STRAPI_LOCALE || "en"}
          title={seo_title}
          bodyId={this.siteClassName}
        />
        {this.preventPageSEOIndexing()}
        <div
          className={`page-sections page category-${category != null ? category.name : "default"}`}
        >
          {(sectionGroups || []).map((el, i) => {
            const components = Array.isArray(el) ? el : [el];
            const componentType = components[0].__typename || components[0].component;
            return (
              <div key={`section-group-${i}`} className={`section-${componentType}`}>
                {(components || []).map((section: Sections, index: number) => {
                  return (
                    <SectionBroker section={section as Section} key={`${index}-${section.id}`} />
                  );
                })}
              </div>
            );
          })}
        </div>
      </Layout>
    );
  }
}

export const query = graphql`
  query DefaultPageQuery($id: ID) {
    cms {
      page(id: $id) {
        data {
          ...PageAttributes
        }
      }
    }
  }
`;
